import React, { useState, useEffect, useCallback, useMemo } from "react";
import { format } from "date-fns";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckIcon, X, FileText } from "lucide-react";
import { useSelector } from "react-redux";
import useDebounce from "utils/hooks/debounce-hook";
import { formatUSDate, formatAppointmentDate } from "utils/date";
import { useNotesByReferral } from "utils/hooks/useNotesByReferral";
import { useCreateReferralNote } from "utils/hooks/useCreateReferralNote";
import { useAppointmentListSlice } from "../../appointment-copy/slice";
import { selectUser } from "app/slice/selectors";
import NotificationsSocket from "./notiSock";
import { selectSocketNotification } from "app/slice";
import { NotificationItem } from "types/Notification";
import { Appointment } from "../../appointment-copy/slice/types";
import { useReferralListSlice } from "app/pages/referral-list/slice";
import { formatSourceDisplay, formatSourceDisplayText, getSourceDisplayClass } from "utils/sourceDisplay";
import { Header } from 'app/components/common/Header.tsx';
import { Table, TableColumn } from "app/components/common/Table/Table";
import { DateFilterDropdown } from "app/components/common/Filters/DateFilterDropdown";
import { DataSummary } from "./DataSummary";
import AppointmentRequestForm from "./AppointmentRequestForm";
import AdminNotes from "./AdminNotes";

import { useToast } from "app/components/ui/use-toast";
import { Button } from "app/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "app/components/ui/dropdown-menu";
import { Calendar as CalendarComponent } from "app/components/ui/calendar";
import { MonthYearDropdown } from 'app/components/common/Filters/MonthYearDropdown';
import { headerStatusOptions, priorityMap, referralMap, referralOptions, reverseStatusMap, statusMap, statusOptions } from "utils/types/table";
import { requestSource, months } from "utils/constants";

const statusBadge = (status: string, isOpen: boolean) => {
  const variant = {
    All: { color: 'bg-[#F2F4F8] text-[#000000]', label: 'All' },
    Scheduled: { color: 'bg-[#DEE3FC] text-[#5B73F1]', label: 'Scheduled' },
    Contacted: { color: 'bg-[#FEF4D3] text-[#F7C525]', label: 'Contacted' },
    Declined: { color: 'bg-[#FFD9D9] text-[#B3171A]', label: 'Declined' },
    "Not contacted": { color: 'bg-[#F2F4F8] text-[#878D96]', label: 'Not contacted' },
  };
  // Convert API status to UI status if needed
  const displayStatus = reverseStatusMap[status] || status;
  const { color, label } = variant[displayStatus] || variant["Not contacted"];
  
  return (
    <span className={`inline-flex items-center justify-between w-[7.125rem] h-[1.4375rem] px-2 rounded-[0.3125rem] text-xs font-medium leading-[0.9rem] whitespace-nowrap ${color} group`}>
      {label}
      <img 
        src="images/chevron-down.svg" 
        alt="" 
        className={`ml-1 transition-opacity opacity-0 group-hover:opacity-100 group-focus:opacity-100 ${isOpen ? 'opacity-100' : 'group-data-[state=open]:hidden'}`}
      />
    </span>
  );
};

interface LocationState {
  selectedReferralId?: string;
  selectedAppointmentId?: string;
  fromNotification?: boolean;
}

// Utility function to deduplicate appointments by _id
const deduplicateAppointments = (appointments: Appointment[]): Appointment[] => {
  const seen = new Set<string>();
  return appointments.filter(appointment => {
    if (seen.has(appointment._id)) {
      return false;
    }
    seen.add(appointment._id);
    return true;
  });
};

const AppointmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { useGetAppointmentsByProviderQuery, useGetNotesByAppointmentQuery, useGetAppointmentSummaryQuery, useUpdateAppointmentMutation } = useAppointmentListSlice();
  const [searchQuery, setSearchQuery] = useState('');
  const [patientTypeFilter, setPatientTypeFilter] = useState<string[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<string[]>([]);
  const [referralFilter, setReferralFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appointmentStartDate, setAppointmentStartDate] = useState<Date | null>(null);
  const [appointmentEndDate, setAppointmentEndDate] = useState<Date | null>(null);
  const [isAppointmentDateFilter, setIsAppointmentDateFilter] = useState<boolean>(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(null);
  const [notificationSelectedId, setNotificationSelectedId] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [isExpandedView, setIsExpandedView] = useState(true);
  const user = useSelector(selectUser)
  const socketNotification = useSelector(selectSocketNotification);
  // return time zone 
  const timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;



  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allAppointments, setAllAppointments] = useState<Appointment[]>([]);
  const [paginationInfo, setPaginationInfo] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalDocuments: 0,
    totalPages: 1,
    hasNextPage: false
  });
  
  // Notification pagination state
  const [notificationCurrentPage, setNotificationCurrentPage] = useState(1);
  const [notificationHasMore, setNotificationHasMore] = useState(true);
  const [isLoadingMoreNotifications, setIsLoadingMoreNotifications] = useState(false);
  const [allNotifications, setAllNotifications] = useState<NotificationItem[]>([]);
  
  const { useChangeAppointmentStatusMutation } = useAppointmentListSlice();
  const {useNotificationListQuery} = useReferralListSlice();
  const { data: notificationData, refetch: refetchNotifications } = useNotificationListQuery({
    page: notificationCurrentPage,
    limit: 20
  });


  const [changeStatus] = useChangeAppointmentStatusMutation();
  const [updateAppointment, { isLoading: isUpdatingAppointment }] = useUpdateAppointmentMutation();

  const { createNote, isCreatingNote } = useCreateReferralNote({ referralId: selected });
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const { toast } = useToast();
  
  // Inline editing state for appointment date
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);
  const [isDateCalendarOpen, setIsDateCalendarOpen] = useState<string | null>(null);
  const [selectedEditDate, setSelectedEditDate] = useState<Date | undefined>(undefined);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [formRefreshTrigger, setFormRefreshTrigger] = useState(0);

  

  // Wrapper functions for DateFilterDropdown interface (individual setters)
  // These handle mutual exclusivity - when user starts selecting dates, clear the other filter
  const handleRequestStartDateChange = useCallback((date: Date | null) => {
    setStartDate(date);
    // When user starts selecting request date (date is not null), clear appointment date filter immediately
    if (date) {
      setAppointmentStartDate(null);
      setAppointmentEndDate(null);
      setIsAppointmentDateFilter(false);
    }
  }, []);

  const handleRequestEndDateChange = useCallback((date: Date | null) => {
    setEndDate(date);
    // When user starts selecting request date (date is not null), clear appointment date filter immediately
    if (date) {
      setAppointmentStartDate(null);
      setAppointmentEndDate(null);
      setIsAppointmentDateFilter(false);
    }
  }, []);

  const handleAppointmentStartDateChange = useCallback((date: Date | null) => {
    setAppointmentStartDate(date);
    // When user starts selecting appointment date (date is not null), clear request date filter immediately
    if (date) {
      setStartDate(null);
      setEndDate(null);
      setIsAppointmentDateFilter(true);
    }
  }, []);

  const handleAppointmentEndDateChange = useCallback((date: Date | null) => {
    setAppointmentEndDate(date);
    // When user starts selecting appointment date (date is not null), clear request date filter immediately
    if (date) {
      setStartDate(null);
      setEndDate(null);
      setIsAppointmentDateFilter(true);
    }
  }, []);

  // Enforce mutual exclusivity and update flag when both dates are set
  useEffect(() => {
    if (startDate && endDate) {
      // Request date filter is fully set - ensure flag is false
      if (isAppointmentDateFilter) {
        setIsAppointmentDateFilter(false);
      }
    } else if (!startDate && !endDate && !appointmentStartDate && !appointmentEndDate) {
      // Both filters are cleared - reset flag
      if (isAppointmentDateFilter) {
        setIsAppointmentDateFilter(false);
      }
    }
  }, [startDate, endDate, appointmentStartDate, appointmentEndDate, isAppointmentDateFilter]);

  useEffect(() => {
    if (appointmentStartDate && appointmentEndDate) {
      // Appointment date filter is fully set - ensure flag is true
      if (!isAppointmentDateFilter) {
        setIsAppointmentDateFilter(true);
      }
    }
  }, [appointmentStartDate, appointmentEndDate, isAppointmentDateFilter]);

  // Memoize query params to prevent unnecessary re-renders
  const queryParams = useMemo(() => {
    const params: any = {
      providerId: user?._id,
      status: statusFilter.length > 0 ? statusFilter : undefined,
      name: debouncedSearchQuery || undefined,
      patientType: patientTypeFilter.length > 0 ? patientTypeFilter : undefined,
      priority: priorityFilter.length > 0 ? priorityFilter : undefined,
      source: referralFilter.length > 0 ? referralFilter : undefined,
      page: currentPage,
      limit: 20,
      timezone: timeZone
    };

    // Only one date filter can be active at a time
    if (isAppointmentDateFilter && appointmentStartDate && appointmentEndDate) {
      // Appointment date filter is active
      params.startDate = format(appointmentStartDate, 'yyyy-MM-dd');
      params.endDate = format(appointmentEndDate, 'yyyy-MM-dd');
      params.appointmentDate = true;
    } else if (!isAppointmentDateFilter && startDate && endDate) {
      // Request date filter is active
      params.startDate = format(startDate, 'yyyy-MM-dd');
      params.endDate = format(endDate, 'yyyy-MM-dd');
      params.appointmentDate = false;
    }

    return params;
  }, [user?._id, statusFilter, debouncedSearchQuery, startDate, endDate, appointmentStartDate, appointmentEndDate, isAppointmentDateFilter, patientTypeFilter, priorityFilter, referralFilter, currentPage]);

  const { data: appointmentsData, isLoading, isError, error, refetch } = useGetAppointmentsByProviderQuery(queryParams);
  
  // Fetch appointment summary data
  const { data: summaryData, isLoading: isSummaryLoading, refetch: refetchSummary } = useGetAppointmentSummaryQuery();

  // Create a stable filter key to detect filter changes
  const filterKey = useMemo(() => {
    // Use the same date fields as queryParams - only one set of dates at a time
    let activeStartDate: Date | null = null;
    let activeEndDate: Date | null = null;
    
    if (isAppointmentDateFilter && appointmentStartDate && appointmentEndDate) {
      activeStartDate = appointmentStartDate;
      activeEndDate = appointmentEndDate;
    } else if (!isAppointmentDateFilter && startDate && endDate) {
      activeStartDate = startDate;
      activeEndDate = endDate;
    }
    
    return JSON.stringify({
      search: debouncedSearchQuery,
      status: statusFilter,
      startDate: activeStartDate?.toISOString(),
      endDate: activeEndDate?.toISOString(),
      isAppointmentDateFilter,
      patientType: patientTypeFilter,
      priority: priorityFilter,
      referral: referralFilter
    });
  }, [debouncedSearchQuery, statusFilter, startDate, endDate, appointmentStartDate, appointmentEndDate, isAppointmentDateFilter, patientTypeFilter, priorityFilter, referralFilter]);

  // Reset pagination when filters change
  useEffect(() => {
    // Reset all pagination and data state atomically
    setCurrentPage(1);
    setAllAppointments([]);
    setSelected(null);
    // Force a refetch to ensure clean data
    refetch();
    refetchSummary();
  }, [filterKey, refetch]);

  // Reset notification pagination when component mounts
  useEffect(() => {
    setNotificationCurrentPage(1);
    setAllNotifications([]);
    setNotificationHasMore(true);
  }, []);

  // Handle appointments data and pagination
  useEffect(() => {
    if (appointmentsData?.data && typeof appointmentsData.data === 'object' && !Array.isArray(appointmentsData.data)) {
      const appointmentResponse = appointmentsData.data as any;
      const { documents, pageNumber, pageSize, totalDocuments, totalPages } = appointmentResponse;
      
      if (documents && Array.isArray(documents)) {
        // Remove duplicates from incoming data using utility function
        const uniqueDocuments = deduplicateAppointments(documents);
        
        // Log warning if duplicates were found in API response
        if (documents.length !== uniqueDocuments.length) {
          console.warn(`Removed ${documents.length - uniqueDocuments.length} duplicate appointments from API response`);
        }
        
        if (pageNumber === 1) {
          // First page - replace all data
          setAllAppointments(uniqueDocuments);
        } else {
          // Subsequent pages - append data with deduplication
          setAllAppointments(prev => {
            const combined = [...prev, ...uniqueDocuments];
            return deduplicateAppointments(combined);
          });
        }
      }
      
      // Update pagination info
      setPaginationInfo({
        pageNumber: pageNumber || 1,
        pageSize: pageSize || 20,
        totalDocuments: totalDocuments || 0,
        totalPages: totalPages || 1,
        hasNextPage: pageNumber < totalPages
      });
    }
  }, [appointmentsData, user?._id]);

  // Handle selection when data changes or filters change
  useEffect(() => {
    if (allAppointments.length > 0) {
      // If no appointment is selected, or the selected appointment is not in the current list, select the first one
      if (!selected || !allAppointments.find(appt => appt._id === selected)) {
        setSelected(allAppointments[0]._id);
      }
    } else if (allAppointments.length === 0 && selected) {
      // If appointments list is empty but we still have a selection, clear it
      setSelected(null);
    }
  }, [allAppointments, selected, filterKey]);

  // Load more function for infinite scroll
  const handleLoadMore = useCallback(() => {
    const canLoadMore = paginationInfo.hasNextPage && !isLoading;
    
    if (canLoadMore) {
      setCurrentPage(prev => prev + 1);
    }
  }, [paginationInfo.hasNextPage, isLoading]);

  // Load more function for notifications
  const handleLoadMoreNotifications = useCallback(() => {
    const canLoadMore = notificationHasMore && !isLoadingMoreNotifications;
    
    if (canLoadMore) {
      setIsLoadingMoreNotifications(true);
      setNotificationCurrentPage(prev => prev + 1);
    }
  }, [notificationHasMore, isLoadingMoreNotifications]);

  // Combined function to merge API and socket notifications with pagination
  const combineNotifications = () => {
    // Handle different response formats for notificationData
    let apiNotifications: NotificationItem[] = [];
    let notificationPagination: { pageNumber: number; pageSize: number; totalDocuments: number; totalPages: number } | null = null;
    
    if (notificationData) {
      if (Array.isArray(notificationData)) {
        apiNotifications = notificationData;
      } else if (typeof notificationData === 'object' && notificationData !== null) {
        const data = notificationData as any;
        // Handle new API structure: data.data.notifications.documents
        const notificationsData = data.data?.notifications?.documents || data.data?.notifications || data.notifications?.documents || data.notifications;
        apiNotifications = Array.isArray(notificationsData) ? notificationsData : [];
        
        // Extract pagination data
        const paginationData = data.data?.notifications || data.notifications;
        if (paginationData && typeof paginationData === 'object') {
          notificationPagination = {
            pageNumber: (paginationData as any).pageNumber || 1,
            pageSize: (paginationData as any).pageSize || 20,
            totalDocuments: (paginationData as any).totalDocuments || 0,
            totalPages: (paginationData as any).totalPages || 1
          };
        }
      }
    }
    
    const socketNotifications = socketNotification ? [socketNotification] : [];
    
    // Ensure both are arrays before spreading
    const socketArray = Array.isArray(socketNotifications) ? socketNotifications : [];
    
    // Handle pagination: append new notifications to existing ones
    if (notificationCurrentPage === 1) {
      // First page - replace all data
      // Filter socket notifications to only include those not present in API with isRead: true
      const filteredSocketArray = socketArray.filter(socketNotification => {
        const apiNotification = apiNotifications.find(api => 
          api._id === socketNotification._id || 
          (api.entityId && api.entityId === socketNotification.entityId)
        );
        
        // If no API notification exists, keep socket notification
        if (!apiNotification) {
          return true;
        }
        
        // If API notification exists and isRead is true, exclude socket notification
        // If API notification exists and isRead is false, keep socket notification (it's newer)
        return apiNotification.isRead !== true;
      });
      
      // Combine API notifications with filtered socket notifications
      const combinedNotifications = [...apiNotifications, ...filteredSocketArray];
      
      // Remove duplicates based on _id or entityId (API data will be first)
      const uniqueNotifications = combinedNotifications.filter((notification, index, self) => 
        index === self.findIndex(n => 
          n._id === notification._id || 
          (n.entityId && n.entityId === notification.entityId)
        )
      );
      
      setNotifications(uniqueNotifications);
      setAllNotifications(uniqueNotifications);
    } else {
      // Subsequent pages - append data
      // Filter socket notifications to only include those not present in API with isRead: true
      const filteredSocketArray = socketArray.filter(socketNotification => {
        const apiNotification = apiNotifications.find(api => 
          api._id === socketNotification._id || 
          (api.entityId && api.entityId === socketNotification.entityId)
        );
        
        // If no API notification exists, keep socket notification
        if (!apiNotification) {
          return true;
        }
        
        // If API notification exists and isRead is true, exclude socket notification
        // If API notification exists and isRead is false, keep socket notification (it's newer)
        return apiNotification.isRead !== true;
      });
      
      // Combine API notifications with existing notifications and filtered socket notifications
      const combinedNotifications = [...apiNotifications, ...allNotifications, ...filteredSocketArray];
      
      // Remove duplicates based on _id or entityId (API data will be first)
      const uniqueNotifications = combinedNotifications.filter((notification, index, self) => 
        index === self.findIndex(n => 
          n._id === notification._id || 
          (n.entityId && n.entityId === notification.entityId)
        )
      );
      
      setNotifications(uniqueNotifications);
      setAllNotifications(uniqueNotifications);
    }
    
    // Update pagination state
    if (notificationPagination) {
      const hasNextPage = notificationPagination.pageNumber < notificationPagination.totalPages;
      setNotificationHasMore(hasNextPage);
    }
    
    // Reset loading more state
    setIsLoadingMoreNotifications(false);
  };

  useEffect(() => {
    combineNotifications();
  }, [notificationData, socketNotification]);

  // Handle navigation state from referral page
  useEffect(() => {
    if (locationState?.selectedAppointmentId && locationState?.fromNotification) {
      const appointmentId = locationState.selectedAppointmentId;
      
      // Wait for data to load, then select the appointment
      if (allAppointments.length > 0) {
        const appointment = allAppointments.find(row => row._id === appointmentId);
        if (appointment) {
          setSelected(appointment._id);
          setNotificationSelectedId(appointment._id);
          
          // Scroll to the selected row
          const selectedRowElement = document.querySelector(`[data-row-id="${appointment._id}"]`);
          if (selectedRowElement) {
            selectedRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          
          toast({
            title: "Appointment Selected",
            description: `Selected appointment for ${appointment.firstName} ${appointment.lastName}`,
          });
          
          // Clear the notification selection after a few seconds
          setTimeout(() => {
            setNotificationSelectedId(null);
          }, 3000);
          
          // Clear the navigation state
          navigate(location.pathname, { replace: true });
        }
      }
    }
  }, [allAppointments, locationState, navigate, location.pathname, toast]);

  // Reset filters when component unmounts
  useEffect(() => {
    return () => {
      setPatientTypeFilter([]);
      setPriorityFilter([]);
      setStatusFilter([]);
      setReferralFilter([]);
      setSearchQuery('');
      setStartDate(null);
      setEndDate(null);
      setAppointmentStartDate(null);
      setAppointmentEndDate(null);
      setIsAppointmentDateFilter(false);
    };
  }, []);

  // Reset collapsible states when selection changes
  useEffect(() => {
    if (selected) {
      setIsFormOpen(true);
      setIsNotesOpen(false);
    }
  }, [selected]);

  // Handle API errors
  useEffect(() => {
    if (isError && error) {
      console.error(' Appointments API Error:', error);
      toast({
        title: "Error Loading Appointments",
        description: error[0] || "Failed to load appointments. Please try again.",
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);

  const handleSearch = (searchTerm: string) => {
    setSearchQuery(searchTerm);
  };

  const handleRowClick = (row: Appointment) => {
    setSelected(row._id);
    // Clear notification selection when user manually selects a row
    setNotificationSelectedId(null);
    // Reset expanded view when selecting a different row
    // setIsExpandedView(false);
  };

  // const handleStatusChange = async (id: string, newStatus: string) => {
  //   try {
  //     // Update local state immediately for better UX
  //     setAllAppointments((prev) =>
  //       prev.map((row) =>
  //         row._id === id ? { ...row, status: statusMap[newStatus] as Appointment['status'] } : row
  //       )
  //     );
      
  //     toast({
  //       title: "Status updated successfully",
  //       description: "The appointment status has been updated.",
  //     });
  //   } catch (error) {
  //     console.error('Failed to update appointment status:', error);
  //     toast({
  //       title: "Failed to update status",
  //       description: "There was an error updating the appointment status.",
  //       variant: "destructive",
  //     });
  //   }
  // };

  const handleAddNote = async (content: string) => {
    const success = await createNote(content);
    if (success) {
      toast({
        title: "Note added successfully",
        description: "Note added successfully",
      });
    } else {
      toast({
        title: "Note added failed",
        description: "Note added failed",
      });
    }
  };

  const handlePatientTypeToggle = (patientType: string) => {
    if (patientType === "All") {
      setPatientTypeFilter([]);
    } else {
      setPatientTypeFilter(prev => {
        if (prev.includes(patientType)) {
          return prev.filter(type => type !== patientType);
        } else {
          return [...prev, patientType];
        }
      });
    }
  };

  const handlePriorityToggle = (priority: string) => {
    if (priority === "All") {
      setPriorityFilter([]);
    } else {
      setPriorityFilter(prev => {
        const priorityValue = priorityMap[priority];
        if (prev.includes(priorityValue)) {
          return prev.filter(p => p !== priorityValue);
        } else {
          return [...prev, priorityValue];
        }
      });
    }
  };

  const handleReferralToggle = (referral: string) => {
    if (referral === "All options") {
      setReferralFilter([]);
    } else {
      setReferralFilter(prev => {
        const referralValue = referralMap[referral];
        if (prev.includes(referralValue)) {
          return prev.filter(r => r !== referralValue);
        } else {
          return [...prev, referralValue];
        }
      });
    }
  }

  const handleStatusToggle = (status: string) => {
    if (status === "All") {
      setStatusFilter([]);
    
    } else {
      setStatusFilter(prev => {
        const newFilter = prev.includes(statusMap[status]) 
          ? prev.filter(s => s !== statusMap[status])
          : [...prev, statusMap[status]];
      
        return newFilter;
      });
    }
  };

  const handleDataSummaryStatusChange = (statuses: string[]) => {
    setStatusFilter(statuses);
    // Automatically set patient type to "new" when status is selected from DataSummary
    if (statuses.length > 0) {
      setPatientTypeFilter(['new']);
    }
  };

  const handleStatusUpdate = async (appointmentId: string, newStatus: string) => {
    try {
      // Show loading state - don't update UI until API confirms
      setStatusDropdownOpen(null);
      
      // Make API call first
      await changeStatus({
        appointmentId: appointmentId as any,
        body: { status: newStatus }
      }).unwrap();
      
      // Only update UI after successful API response
      setAllAppointments((prev) =>
        prev.map((row) =>
          row._id === appointmentId ? { ...row, status: newStatus as Appointment['status'] } : row
        )
      );
      
      // Force refetch to ensure data consistency
      refetch();
      refetchSummary();
      
      // Trigger form refresh if this appointment is currently selected
      if (appointmentId === selected) {
        setFormRefreshTrigger(prev => prev + 1);
      }
      
      toast({
        title: "Status updated successfully",
        description: "The appointment status has been updated.",
      });
    } catch (error: any) {
      toast({
        title: "Failed to update status",
        description: error?.data?.message || error?.message || "There was an error updating the appointment status.",
        variant: "destructive",
      });
      console.error('Failed to update appointment status:', error);
    }
  };

  // Add new handlers for panel state management
  const handleFormOpen = (open: boolean) => {
    setIsFormOpen(open);
    if (open) {
      setIsNotesOpen(false);
    }
  };

  const handleNotesOpen = (open: boolean) => {
    setIsNotesOpen(open);
    if (open) {
      setIsFormOpen(false);
    }
  };

  const handleApiError = (error: any) => {
    toast({
      title: "Date Filter Error",
      description: error?.data?.message || error?.message || "Invalid date range selected. Please try again.",
      variant: "destructive",
    });
  };

  // Handle inline date editing
  const handleDateEditClick = (appointmentId: string, currentDate: string | undefined) => {
    setEditingAppointmentId(appointmentId);
    setIsDateCalendarOpen(appointmentId);
    if (currentDate) {
      const date = new Date(currentDate);
      setSelectedEditDate(date);
      setSelectedMonth(date.getMonth());
      setSelectedYear(date.getFullYear());
    } else {
      setSelectedEditDate(undefined);
      setSelectedMonth(new Date().getMonth());
      setSelectedYear(new Date().getFullYear());
    }
  };

  const handleDateChange = async (appointmentId: string, day: Date) => {
    setSelectedEditDate(day);
    setIsDateCalendarOpen(null);
    
    const formatDateToLocalISO = (date: Date): string => {
      const now = new Date();
      const combinedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds()
      );
      return combinedDate.toISOString();
    };

    try {
      const appointment = allAppointments.find(apt => apt._id === appointmentId);
      if (!appointment) return;

      const payload = {
        patientType: appointment.patientType,
        firstName: appointment.firstName,
        lastName: appointment.lastName,
        preferredDays: appointment.preferredDays || [],
        preferredTimes: appointment.preferredTimes || [],
        servicesInterested: appointment.servicesInterested || '',
        message: appointment.message || '',
        source: appointment.source || [],
        ...(appointment.phoneNumber && { phoneNumber: appointment.phoneNumber }),
        ...(appointment.email && { email: appointment.email }),
        date: formatDateToLocalISO(day),
        ...(appointment.requestSource && { requestSource: appointment.requestSource }),
      };

      await updateAppointment({ appointmentId, body: payload }).unwrap();
      
      // Update local state
      setAllAppointments((prev) =>
        prev.map((row) =>
          row._id === appointmentId ? { ...row, date: day.toISOString() } : row
        )
      );
      
      toast({
        title: 'Appointment updated successfully',
        description: 'The appointment date has been updated.',
      });
      
      // Refetch to ensure data consistency
      refetch();
      refetchSummary();
      
      // Trigger form refresh if this appointment is currently selected
      if (appointmentId === selected) {
        setFormRefreshTrigger(prev => prev + 1);
      }
    } catch (err: any) {
      toast({
        title: 'Failed to update appointment',
        description: err?.data?.message || err?.message || 'Failed to update appointment date',
        variant: 'destructive',
      });
    } finally {
      setEditingAppointmentId(null);
      setSelectedEditDate(undefined);
    }
  };

  const handleCancelDateEdit = () => {
    setEditingAppointmentId(null);
    setIsDateCalendarOpen(null);
    setSelectedEditDate(undefined);
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i);

  const handleNotificationSelect = (entityId: string | null) => {
    if (entityId) {
      // Find the appointment with matching ID in the current data
      const appointment = allAppointments.find(row => row._id === entityId || row.id === entityId);
      
      if (appointment) {
        // Select the appointment
        setSelected(appointment._id);
        setNotificationSelectedId(appointment._id);
        
        // Scroll to the selected row if needed
        const selectedRowElement = document.querySelector(`[data-row-id="${appointment._id}"]`);
        if (selectedRowElement) {
          selectedRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Clear the notification selection after a few seconds
        setTimeout(() => {
          setNotificationSelectedId(null);
        }, 3000);
      } else {
        // Check if this notification is for a referral instead
        const notification = notifications.find(n => n.entityId === entityId);
        
        if (notification?.notificationType === 'Referral' || notification?.type === 'referral') {
          // Navigate to referral page with the entityId
          navigate('/provider-referrals', { 
            state: { 
              selectedReferralId: entityId,
              fromNotification: true 
            } 
          });
        } else {
          // If appointment not found in current data, it might be filtered out
          toast({
            title: "Appointment Not Found",
            description: "The selected appointment is not visible in the current view. Clearing filters to show all appointments.",
            variant: "destructive",
          });
          
          // Clear filters to show all appointments - this will trigger the filter reset effect
          setPatientTypeFilter([]);
          setPriorityFilter([]);
          setStatusFilter([]);
          setSearchQuery('');
          setStartDate(null);
          setEndDate(null);
          setAppointmentStartDate(null);
          setAppointmentEndDate(null);
          setIsAppointmentDateFilter(false);
        }
      }
    }
  };

  // Function to refetch notifications after marking as read
  const handleNotificationRead = useCallback(() => {
    // Refetch notifications to get updated read/unread counts
    refetchNotifications();
  }, [refetchNotifications]);

  // Create simplified columns for expanded view
  const createExpandedColumns = (): TableColumn<Appointment>[] => [
    { 
      key: "date", 
      header: () => (
        <DateFilterDropdown
          startDate={startDate}
          endDate={endDate}
          setStartDate={handleRequestStartDateChange}
          setEndDate={handleRequestEndDateChange}
          onApiError={handleApiError}
        />
      ),
      width: '4.75rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal flex items-center justify-start 2xl:justify-center">
          {row.date ? formatUSDate(row.date) : row.createdAt ? formatUSDate(row.createdAt) : '-'}
        </div>
      )
    },
    { 
      key: "firstName", 
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <h3 className="capitalize text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
           First Name
          </h3>
        </div>
      ),
      width: '6.375rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-2 xl:pl-4 2xl:pl-5 justify-start flex items-center 2xl:justify-center">
          {row.firstName || ''}
        </div>
      )
    },
    { 
      key: "lastName", 
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <h3 className="capitalize text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            Last Name
          </h3>
        </div>
      ),
      width: '6.375rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-2 xl:pl-4 2xl:pl-5 flex items-center justify-start 2xl:justify-center">
          {row.lastName || ''}
        </div>
      )
    },
    {
      key: 'patientType',
      header: () => (
        <div className="flex gap-2 items-center justify-center pl-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center hover:bg-[#F2F4F8] w-[7rem] rounded py-1 cursor-pointer ${patientTypeFilter.length > 0 ? 'bg-[#F2F4F8] gap-1' : 'gap-2'} data-[state=open]:bg-[#F2F4F8]`}>
                {patientTypeFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="capitalize text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.015rem] whitespace-nowrap">
                 Patient Type
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7rem] max-w-[7rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              sideOffset={2}
            >
              <DropdownMenuItem
                className={`flex gap-1.5 items-center group px-2 py-1.5 border-b border-[#F2F4F8] cursor-pointer ${patientTypeFilter.length === 0 ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                onSelect={() => handlePatientTypeToggle('All')}
              >
                <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${patientTypeFilter.length === 0 ? 'bg-[#5B73F1]' : ''}`}>
                  {patientTypeFilter.length === 0 && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem] whitespace-nowrap">
                  All
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`flex gap-1.5 items-center group px-2 py-1.5 border-b border-[#F2F4F8] cursor-pointer ${patientTypeFilter.includes('new') ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                onSelect={() => handlePatientTypeToggle('new')}
              >
                <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${patientTypeFilter.includes('new') ? 'bg-[#5B73F1]' : ''}`}>
                  {patientTypeFilter.includes('new') && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem] whitespace-nowrap">
                  New Patient
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`flex gap-1.5 items-center group px-2 py-1.5 border-b border-[#F2F4F8] cursor-pointer ${patientTypeFilter.includes('existing') ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                onSelect={() => handlePatientTypeToggle('existing')}
              >
                <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${patientTypeFilter.includes('existing') ? 'bg-[#5B73F1]' : ''}`}>
                  {patientTypeFilter.includes('existing') && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem] whitespace-nowrap">
                  Existing Patient
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '7.125rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-5 xl:pl-6 2xl:pl-0 flex items-center justify-start 2xl:justify-center">
          {row.patientType === 'new' ? 'New' : 'Existing'}
        </div>
      ),
    },
    {
      key: 'source',
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center hover:bg-[#F2F4F8]  py-1 rounded cursor-pointer ${referralFilter.length > 0 ? 'bg-[#F2F4F8] gap-0.5' : 'gap-1'} data-[state=open]:bg-[#F2F4F8]`}>
                {referralFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="capitalize text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
                  Referral
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7.375rem] max-w-[7.375rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              align="start"
              sideOffset={2}
            >
              {referralOptions.map((option) => (
                <DropdownMenuItem
                  key={option}
                  className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${(option === "All options" && referralFilter.length === 0) || (option !== "All options" && referralFilter.includes(referralMap[option])) ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                  onSelect={() => handleReferralToggle(option)}
                >
                  <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${option === "All options" ? (referralFilter.length === 0 ? 'bg-[#5B73F1]' : '') : (referralFilter.includes(referralMap[option]) ? 'bg-[#5B73F1]' : '')}`}>
                    {(option === "All options" && referralFilter.length === 0) || (option !== "All options" && referralFilter.includes(referralMap[option])) ? (
                      <CheckIcon className="w-3 h-3 text-white" />
                    ) : null}
                  </div>
                  <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                    {option}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '5.8125rem',
      render: (row) => {
        const displaySource = formatSourceDisplay(row.source, { patientTypeFilter });
        const displayText = formatSourceDisplayText(displaySource);
        const displayClass = getSourceDisplayClass(displaySource);

        return (
          <div className={displayClass}>
            {
              <span className="capitalize flex items-center justify-start pl-2 xl:pl-4 2xl:pl-0 2xl:justify-center">{displayText}</span>
            }
          </div>
        );
      },
    },
    {
      key: 'status',
      header: () => (
        <div className="flex items-center pl-1.5 justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center gap-2 hover:bg-[#F2F4F8] w-[7rem] rounded py-1 cursor-pointer ${statusFilter.length > 0 ? 'bg-[#F2F4F8]' : ''} data-[state=open]:bg-[#F2F4F8]`}>
                {statusFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="capitalize text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
                  Status
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7rem] max-w-[7rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              sideOffset={2}
            >
              {headerStatusOptions.map((option) => (
                <DropdownMenuItem
                  key={option}
                  className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${(option === "All" && statusFilter.length === 0) || (option !== "All" && statusFilter.includes(statusMap[option])) ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                  onSelect={() => handleStatusToggle(option)}
                >
                  <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${option === "All" ? (statusFilter.length === 0 ? 'bg-[#5B73F1]' : '') : (statusFilter.includes(statusMap[option]) ? 'bg-[#5B73F1]' : '')}`}>
                    {(option === "All" && statusFilter.length === 0) || (option !== "All" && statusFilter.includes(statusMap[option])) ? (
                      <CheckIcon className="w-3 h-3 text-white" />
                    ) : null}
                  </div>
                  <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                    {option}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '7.125rem',
      render: (row) => {
        const status = row.status;
        return (
          <div className="pl-1.5 flex items-center justify-start 2xl:justify-center">
            <DropdownMenu open={statusDropdownOpen === row._id} onOpenChange={(open) => setStatusDropdownOpen(open ? row._id : null)}>
              <DropdownMenuTrigger asChild>
                <button 
                  type="button" 
                  className="focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {statusBadge(status, statusDropdownOpen === row._id)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="min-w-[7rem] max-w-[7rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                sideOffset={2}
              >
                {statusOptions.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${reverseStatusMap[status] === option ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                    onSelect={() => handleStatusUpdate(row._id, statusMap[option])}
                  >
                    <div className={`flex-shrink-0 flex items-center justify-start 2xl:justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${reverseStatusMap[status] === option ? 'bg-[#5B73F1]' : ''}`}>
                      {reverseStatusMap[status] === option && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                      {option}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
    {
      key: 'appointmentDate',
      header: () => (
        <DateFilterDropdown
          startDate={appointmentStartDate}
          endDate={appointmentEndDate}
          setStartDate={handleAppointmentStartDateChange}
          setEndDate={handleAppointmentEndDateChange}
          onApiError={handleApiError}
          headerText="Appt. Date"
        />
      ),
      width: '6.125rem',
      render: (row) => {
        const isEditing = editingAppointmentId === row._id;
        const appointmentDate = row.date ?? undefined;
        const calendarMonth = new Date(selectedYear, selectedMonth, 1);
        
        return (
          <div className="text-[#21272A] text-sm font-normal leading-normal pl-1.5 flex items-center justify-center 2xl:justify-center">
            {isEditing ? (
              <DropdownMenu 
                open={isDateCalendarOpen === row._id} 
                onOpenChange={(open) => {
                  if (!open) {
                    handleCancelDateEdit();
                  } else {
                    setIsDateCalendarOpen(row._id);
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex gap-1 items-center focus:outline-none hover:bg-[#F2F4F8] px-2 py-1 rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isEditing) {
                        handleDateEditClick(row._id, appointmentDate);
                      }
                    }}
                  >
                    {selectedEditDate ? (
                      <span className="inline-block text-[#343A3F] text-sm font-normal leading-[1.0625rem]">
                        {formatUSDate(selectedEditDate)}
                      </span>
                    ) : (
                      <img src="/images/calendar-icon.svg" alt="Calendar" className="size-4" />
                    )}
                    {/* <img src="/images/chevron-down.svg" alt="Chevron" className="size-3" /> */}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="p-0 bg-white border border-[#DDE1E6] rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                  sideOffset={4}
                  align="start"
                >
                  <div className="relative">
                    <div className="flex absolute top-4 right-4 z-10 gap-4 justify-end items-center">
                      <MonthYearDropdown
                        selectedValue={months[selectedMonth]}
                        setSelectedValue={(value) => {
                          setSelectedMonth(months.indexOf(value as string));
                          return true;
                        }}
                        options={months}
                        type="month"
                      />
                      <MonthYearDropdown
                        selectedValue={selectedYear}
                        setSelectedValue={(value) => setSelectedYear(value as number)}
                        options={years}
                        type="year"
                      />
                    </div>
                    <CalendarComponent
                      mode="single"
                      month={calendarMonth}
                      selected={selectedEditDate}
                      onDayClick={(day) => handleDateChange(row._id, day)}
                      className="bg-white"
                      classNames={{
                        nav: "hidden",
                        caption_label: "hidden",
                      }}
                    />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                type="button"
                className="flex items-center gap-1 hover:bg-[#F2F4F8] px-2 py-1 rounded cursor-pointer group"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDateEditClick(row._id, appointmentDate);
                }}
                title="Click to edit appointment date"
              >
                <span>{appointmentDate ? formatAppointmentDate(appointmentDate) : <img src="/images/calendar-day.svg" alt="Calendar" className="size-4" />}</span>
                {/* <img src="/images/edits.svg" alt="Edit" className="size-3 opacity-60 group-hover:opacity-100" /> */}
              </button>
            )}
          </div>
        );
      }
    },
  ];

  const columns: TableColumn<Appointment>[] = [
    { 
      key: "date", 
      header: () => (
        <DateFilterDropdown
          startDate={startDate}
          endDate={endDate}
          setStartDate={handleRequestStartDateChange}
          setEndDate={handleRequestEndDateChange}
          onApiError={handleApiError}
        />
      ),
      width: '5.1875rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal whitespace-nowrap flex items-center justify-center">
          {row.date ? formatUSDate(row.date) : (row?.createdAt ? formatUSDate(row.createdAt) : '-')}
        </div>
      )
    },
    { 
      key: "firstName", 
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            First Name
          </h3>
        </div>
      ),
      width: '6.375rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-1.5 flex items-center justify-center">
          {row.firstName || ''}
        </div>
      )
    },
    { 
      key: "lastName", 
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            Last Name
          </h3>
        </div>
      ),
      width: '6.375rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-1.5 flex items-center justify-center">
          {row.lastName || ''}
        </div>
      )
    },
    
    {
      key: 'patientType',
      header: () => (
        <div className="flex gap-2 items-center justify-center pl-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center hover:bg-[#F2F4F8] w-[7.125rem] rounded py-1 cursor-pointer ${patientTypeFilter.length > 0 ? 'bg-[#F2F4F8] gap-1' : 'gap-2'} data-[state=open]:bg-[#F2F4F8]`}>
                {patientTypeFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.015rem] whitespace-nowrap">
                  Patient Type
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7.125rem] max-w-[7.125rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              sideOffset={2}
            >
              <DropdownMenuItem
                className={`flex gap-1.5 items-center group px-2 py-1.5 border-b border-[#F2F4F8] cursor-pointer ${patientTypeFilter.length === 0 ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                onSelect={() => handlePatientTypeToggle('All')}
              >
                <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${patientTypeFilter.length === 0 ? 'bg-[#5B73F1]' : ''}`}>
                  {patientTypeFilter.length === 0 && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem] whitespace-nowrap">
                  All
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`flex gap-1.5 items-center group px-2 py-1.5 border-b border-[#F2F4F8] cursor-pointer ${patientTypeFilter.includes('new') ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                onSelect={() => handlePatientTypeToggle('new')}
              >
                <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${patientTypeFilter.includes('new') ? 'bg-[#5B73F1]' : ''}`}>
                  {patientTypeFilter.includes('new') && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem] whitespace-nowrap">
                  New Patient
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`flex gap-1.5 items-center group px-2 py-1.5 border-b border-[#F2F4F8] cursor-pointer ${patientTypeFilter.includes('existing') ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                onSelect={() => handlePatientTypeToggle('existing')}
              >
                <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${patientTypeFilter.includes('existing') ? 'bg-[#5B73F1]' : ''}`}>
                  {patientTypeFilter.includes('existing') && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem] whitespace-nowrap">
                  Existing Patient
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '7.125rem',
      render: (row) => (
        <div className="text-[#21272A] text-sm font-normal leading-normal pl-6 flex items-center justify-center">
          {row.patientType === 'new' ? 'New' : 'Existing'}
        </div>
      ),
    },
    { 
      key: "requestSource", 
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            Req. Source
          </h3>
        </div>
      ),
      width: '7.5rem',
      render: (row) => {
      
        return (
          <div className="text-[#21272A] text-sm font-normal leading-normal pl-1.5 flex items-center justify-center">
            {requestSource[row.requestSource as unknown as keyof typeof requestSource] || '-'}
          </div>
        );
      }
    },
    {
      key: 'source',
      header: () => (
        <div className="flex items-center justify-center pl-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center hover:bg-[#F2F4F8]  py-1 rounded cursor-pointer ${referralFilter.length > 0 ? 'bg-[#F2F4F8] gap-0.5' : 'gap-1'} data-[state=open]:bg-[#F2F4F8]`}>
                {referralFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
                  Referral
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7.375rem] max-w-[7.375rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              align="start"
              sideOffset={2}
            >
              {referralOptions.map((option) => (
                <DropdownMenuItem
                  key={option}
                  className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${(option === "All options" && referralFilter.length === 0) || (option !== "All options" && referralFilter.includes(referralMap[option])) ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                  onSelect={() => handleReferralToggle(option)}
                >
                  <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${option === "All options" ? (referralFilter.length === 0 ? 'bg-[#5B73F1]' : '') : (referralFilter.includes(referralMap[option]) ? 'bg-[#5B73F1]' : '')}`}>
                    {(option === "All options" && referralFilter.length === 0) || (option !== "All options" && referralFilter.includes(referralMap[option])) ? (
                      <CheckIcon className="w-3 h-3 text-white" />
                    ) : null}
                  </div>
                  <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                    {option}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '5.8125rem',
      render: (row) => {
        const displaySource = formatSourceDisplay(row.source, { patientTypeFilter });
        const displayText = formatSourceDisplayText(displaySource);
        const displayClass = getSourceDisplayClass(displaySource);

        return (
          <div className={displayClass}>
            {displayText === 'Friends or Family' ? 
              displayText : 
              <span className="capitalize flex items-center justify-center">{displayText}</span>
            }
          </div>
        );
      },
    },
    {
      key: 'status',
      header: () => (
        <div className="flex items-center justify-center pl-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className={`flex justify-center items-center gap-2 hover:bg-[#F2F4F8] w-[7.125rem] rounded py-1 cursor-pointer ${statusFilter.length > 0 ? 'bg-[#F2F4F8]' : ''} data-[state=open]:bg-[#F2F4F8]`}>
                {statusFilter.length > 0 && (
                  <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                    <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                  </svg>
                )}
                <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem]">
                  Status
                </h3>
                <img src="images/chevron-down.svg" alt="" className="mt-px" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-[7.125rem] max-w-[7.125rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
              sideOffset={2}
            >
              {headerStatusOptions.map((option) => (
                <DropdownMenuItem
                  key={option}
                  className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${(option === "All" && statusFilter.length === 0) || (option !== "All" && statusFilter.includes(statusMap[option])) ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                  onSelect={() => handleStatusToggle(option)}
                >
                  <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${option === "All" ? (statusFilter.length === 0 ? 'bg-[#5B73F1]' : '') : (statusFilter.includes(statusMap[option]) ? 'bg-[#5B73F1]' : '')}`}>
                    {(option === "All" && statusFilter.length === 0) || (option !== "All" && statusFilter.includes(statusMap[option])) ? (
                      <CheckIcon className="w-3 h-3 text-white" />
                    ) : null}
                  </div>
                  <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                    {option}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      width: '7.125rem',
      render: (row) => {
        const status = row.status;
        return (
          <div className="pl-3 flex items-center justify-center">
            <DropdownMenu open={statusDropdownOpen === row._id} onOpenChange={(open) => setStatusDropdownOpen(open ? row._id : null)}>
              <DropdownMenuTrigger asChild>
                <button 
                  type="button" 
                  className="focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {statusBadge(status, statusDropdownOpen === row._id)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="min-w-[7.125rem] max-w-[7.125rem] bg-white p-0 border-none rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                sideOffset={2}
              >
                {statusOptions.map((option) => (
                  <DropdownMenuItem
                    key={option}
                    className={`flex gap-1.5 items-center group px-2 py-1.5 cursor-pointer ${reverseStatusMap[status] === option ? 'focus:bg-white' : 'focus:bg-[#DDE1E6]'}`}
                    onSelect={() => handleStatusUpdate(row._id, statusMap[option])}
                  >
                    <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${reverseStatusMap[status] === option ? 'bg-[#5B73F1]' : ''}`}>
                      {reverseStatusMap[status] === option && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="text-black text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">
                      {option}
                    </span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
    {
      key: 'servicesInterested',
      header: () => (
        <div className="flex items-center justify-center px-1.5">
          <h3 className="uppercase text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
            Service/Procedure
          </h3>
        </div>
      ),
      width: '10rem',
      render: (row) => {
        const hasData = row.servicesInterested || row.procedure;
        return (
          <div className={`text-[#21272A] text-sm font-normal leading-normal px-1.5 flex items-center ${hasData ? 'justify-center' : 'justify-center'}`}>
            {hasData || '-'}
          </div>
        );
      }
    },
    {
      key: 'appointmentDate',
      header: () => (
        <DateFilterDropdown
          startDate={appointmentStartDate}
          endDate={appointmentEndDate}
          setStartDate={handleAppointmentStartDateChange}
          setEndDate={handleAppointmentEndDateChange}
          onApiError={handleApiError}
          headerText="Appt. Date"
        />
      ),
      width: '8rem',
      responsiveWidth: '6rem',
      render: (row) => {
        const isEditing = editingAppointmentId === row._id;
        const appointmentDate = row.date ?? undefined;
        const calendarMonth = new Date(selectedYear, selectedMonth, 1);
        
        return (
          <div className="text-[#21272A] text-sm font-normal leading-normal pl-1.5 flex items-center justify-center">
            {isEditing ? (
              <DropdownMenu 
                open={isDateCalendarOpen === row._id} 
                onOpenChange={(open) => {
                  if (!open) {
                    handleCancelDateEdit();
                  } else {
                    setIsDateCalendarOpen(row._id);
                  }
                }}
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex gap-1 items-center focus:outline-none hover:bg-[#F2F4F8] px-2 py-1 rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isEditing) {
                        handleDateEditClick(row._id, appointmentDate);
                      }
                    }}
                  >
                    {selectedEditDate ? (
                      <span className="inline-block text-[#343A3F] text-sm font-normal leading-[1.0625rem]">
                        {formatUSDate(selectedEditDate)}
                      </span>
                    ) : (
                      <img src="/images/calendar-icon.svg" alt="Calendar" className="size-4" />
                    )}
                    {/* <img src="/images/chevron-down.svg" alt="Chevron" className="size-3" /> */}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="p-0 bg-white border border-[#DDE1E6] rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                  sideOffset={4}
                  align="start"
                >
                  <div className="relative">
                    <div className="flex absolute top-4 right-4 z-10 gap-4 justify-end items-center">
                      <MonthYearDropdown
                        selectedValue={months[selectedMonth]}
                        setSelectedValue={(value) => {
                          setSelectedMonth(months.indexOf(value as string));
                          return true;
                        }}
                        options={months}
                        type="month"
                      />
                      <MonthYearDropdown
                        selectedValue={selectedYear}
                        setSelectedValue={(value) => setSelectedYear(value as number)}
                        options={years}
                        type="year"
                      />
                    </div>
                    <CalendarComponent
                      mode="single"
                      month={calendarMonth}
                      selected={selectedEditDate}
                      onDayClick={(day) => handleDateChange(row._id, day)}
                      className="bg-white"
                      classNames={{
                        nav: "hidden",
                        caption_label: "hidden",
                      }}
                    />
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                type="button"
                className="flex items-center gap-1 hover:bg-[#F2F4F8] px-2 py-1 rounded cursor-pointer group"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDateEditClick(row._id, appointmentDate);
                }}
                title="Click to edit appointment date"
              >
                <span>{appointmentDate ? formatAppointmentDate(appointmentDate) :  <img src="/images/calendar-icon.svg" alt="Calendar" className="size-4" />}</span>
                {/* <img src="/images/edits.svg" alt="Edit" className="size-3 opacity-60 group-hover:opacity-100" /> */}
              </button>
            )}
          </div>
        );
      }
    },
    {
      key: 'notesCount',
      header: () => <></>,
      width: '5rem',
      render: (row) => (
        <div className="flex items-center gap-1 pl-1.5 justify-center">
          <FileText 
            size={15} 
            className={selected === row._id ? "text-blue-500" : "text-[#697077]"} 
          />
          <span className="text-[#21272A] text-sm font-normal leading-normal flex items-center justify-center">
            {row.notesCount || 0}
          </span>
        </div>
      )
    },
    {
      key: 'expand',
      header: () => <></>,
      width: '4.75rem',
      render: (row) => (
        <div className="flex justify-center items-center">
          <Button
            type="button"
            variant="ghost"
            className="bg-[#F2F4F8] hover:bg-[#DDE1E6] size-[1.375rem] rounded transition-colors group"
            onClick={(e) => {
              e.stopPropagation();
              setSelected(row._id);
              // setIsExpandedView(!isExpandedView);
              setIsPanelOpen(true);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="7"
              height="11"
              viewBox="0 0 7 11"
              fill="none"
              className="text-[#697077] group-hover:text-[#374151]"
            >
              <path
                d="M1.0752 9.3501L4.9252 5.5001L1.07519 1.6501"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Header 
        onSearch={handleSearch} 
        notifications={notifications} 
        onNotificationSelect={handleNotificationSelect}
        onNotificationRead={handleNotificationRead}
        notificationPagination={{
          pageNumber: notificationCurrentPage,
          pageSize: 20,
          totalDocuments: 0, // This will be updated from API response
          totalPages: 1
        }}
        onLoadMoreNotifications={handleLoadMoreNotifications}
        notificationHasMore={notificationHasMore}
        isLoadingMoreNotifications={isLoadingMoreNotifications}
        paddingBottom="pb-16"
      />
      <NotificationsSocket onAppointmentNotification={() => {
        refetch();
        refetchSummary();
        toast({
          title: "Appointments Updated",
          description: "Appointment list has been refreshed with latest data.",
        });
      }} />
      <div className="flex-1 pt-[8.5625rem]">
        <div className="h-[calc(100vh-6.5rem)] p-6 overflow-y-auto custom-scrollbar">
          <DataSummary summary={summaryData?.data} onStatusChange={handleDataSummaryStatusChange} />
          <div className="flex gap-6 bg-[#F2F4F8] overflow-x-auto [&::-webkit-scrollbar]:hidden">
            {/* Left: Table */}
            <div className={`flex-grow flex-shrink-0 ${isPanelOpen ? 'min-w-[40rem]' : 'min-w-full'}`}>
              <Table<Appointment>
                key={`${filterKey}-${isExpandedView}`} // Force re-render when filters or view change
                columns={isExpandedView ? createExpandedColumns() : columns}
                data={allAppointments}
                isLoading={isLoading}
                hasMore={paginationInfo.hasNextPage}
                onLoadMore={handleLoadMore}
                rowKey={(row) => row._id}
                emptyMessage="No appointments found."
                onRowClick={handleRowClick}
                selectedId={selected}
                selectedStyles={notificationSelectedId === selected ? 'bg-[#DDE1E680] hover:bg-[#DDE1E690] border-l-2 border-l-[#55B494]' : 'bg-[#DDE1E680] hover:bg-[#DDE1E690] border-l-2'}
                tableHeight="h-[calc(100vh-14rem)]"
              />
            </div>
            {/* Right: Details Panel */}
            {isPanelOpen && (
              <div className="flex-shrink-0 flex flex-col gap-3 w-[clamp(27.5625rem,calc(100vw-67.35rem),45rem)]">
                {/* <div className="flex justify-end">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    className="cursor-pointer text-[#878D96] hover:text-[#374151]"
                    // onClick={() => {
                    //   setIsPanelOpen(false);
                    //   setIsExpandedView(false);
                    // }}
                  >
                    <path
                      d="M11.7208 0.279164C11.542 0.100415 11.2996 0 11.0467 0C10.7939 0 10.5514 0.100415 10.3726 0.279164L6 4.65179L1.62737 0.279164C1.44857 0.100415 1.2061 0 0.953269 0C0.700442 0 0.457967 0.100415 0.279164 0.279164C0.100415 0.457967 0 0.700442 0 0.953269C0 1.2061 0.100415 1.44857 0.279164 1.62737L4.65179 6L0.279164 10.3726C0.100415 10.5514 0 10.7939 0 11.0467C0 11.2996 0.100415 11.542 0.279164 11.7208C0.457967 11.8996 0.700442 12 0.953269 12C1.2061 12 1.44857 11.8996 1.62737 11.7208L6 7.34821L10.3726 11.7208C10.5514 11.8996 10.7939 12 11.0467 12C11.2996 12 11.542 11.8996 11.7208 11.7208C11.8996 11.542 12 11.2996 12 11.0467C12 10.7939 11.8996 10.5514 11.7208 10.3726L7.34821 6L11.7208 1.62737C11.8996 1.44857 12 1.2061 12 0.953269C12 0.700442 11.8996 0.457967 11.7208 0.279164Z"
                      fill="currentColor"
                    />
                  </svg>
                </div> */}
                <AppointmentRequestForm
                  isFormOpen={isFormOpen}
                  isNotesOpen={isNotesOpen}
                  setIsFormOpen={handleFormOpen}
                  appointmentId={selected || ''}
                  refreshTrigger={formRefreshTrigger}
                />
                <AdminNotes
                  isFormOpen={isFormOpen}
                  isNotesOpen={isNotesOpen}
                  setIsNotesOpen={handleNotesOpen}
                  appointmentId={allAppointments.length > 0 ? (selected || '') : ''}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AppointmentListPage;
